import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { TopNav } from "@/components/TopNav";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Coins, FolderOpen, Infinity as Inf, ArrowUpRight, Crown, Trash2, RotateCcw } from "lucide-react";

export default function Dashboard() {
  const { user, isPrivileged } = useAuth();
  const nav = useNavigate();
  const [projects, setProjects] = useState([]);
  const [profile, setProfile] = useState(null);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [creating, setCreating] = useState(false);

  const load = async () => {
    const [p, pr] = await Promise.all([api.get("/projects"), api.get("/me/profile")]);
    setProjects(p.data); setProfile(pr.data);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!name.trim()) return;
    setCreating(true);
    try {
      const r = await api.post("/projects", { name, description: desc });
      setOpen(false); setName(""); setDesc("");
      nav(`/studio/${r.data.id}`);
    } catch (e) { toast.error("Could not create project"); } finally { setCreating(false); }
  };

  const del = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm("Delete this project permanently? This removes its brief, DNA, plan and builds.")) return;
    try { await api.delete(`/projects/${id}`); toast.success("Project deleted"); load(); }
    catch { toast.error("Could not delete project"); }
  };
  const reset = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm("Reset this project? Clears DNA, plan, session and builds (keeps your brief).")) return;
    try { await api.post(`/projects/${id}/reset`); toast.success("Project reset"); load(); }
    catch { toast.error("Could not reset project"); }
  };

  const bal = profile?.balance;
  const hasPlan = isPrivileged || user?.subscription_status === "active" || (bal && typeof bal.available === "number" && bal.available > 0);

  return (
    <div className="min-h-screen">
      <TopNav />
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-10">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-9">
          <div>
            <p className="label-mono text-cyan mb-2">Welcome back</p>
            <h1 className="font-display font-extrabold tracking-tighter text-3xl sm:text-4xl text-slate-50">{user?.name?.split(" ")[0] || "Producer"}'s studio</h1>
          </div>
          <div className="flex items-center gap-3">
            <div data-testid="credit-summary" className="card-surface rounded-xl px-5 py-3 flex items-center gap-3">
              <Coins className="w-5 h-5 text-amber" />
              <div>
                <p className="label-mono text-slate-500">Credits</p>
                <p className="font-display font-bold text-lg text-slate-50 flex items-center gap-1">
                  {isPrivileged ? (<><Inf className="w-4 h-4 text-cyan" /> Unlimited</>) : (bal?.available?.toLocaleString?.() ?? 0)}
                </p>
              </div>
            </div>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="new-project-btn" className="rounded-full h-12 px-6 bg-cyan-400 text-black hover:bg-cyan-300 font-semibold">
                  <Plus className="w-4 h-4 mr-1.5" /> New session
                </Button>
              </DialogTrigger>
              <DialogContent className="card-surface border-white/10">
                <DialogHeader><DialogTitle className="font-display text-xl">New session</DialogTitle>
                  <DialogDescription className="text-slate-400">Name your track idea, you'll feed the brief next.</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  <div>
                    <label className="label-mono text-slate-400">Project name</label>
                    <Input data-testid="project-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Midnight Drive" className="mt-1.5 bg-white/5 border-white/10" />
                  </div>
                  <div>
                    <label className="label-mono text-slate-400">Description (optional)</label>
                    <Textarea data-testid="project-desc-input" value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Dark synthwave, 808s, cinematic…" className="mt-1.5 bg-white/5 border-white/10" />
                  </div>
                </div>
                <DialogFooter>
                  <Button data-testid="create-project-confirm" onClick={create} disabled={creating} className="rounded-full bg-cyan-400 text-black hover:bg-cyan-300 font-semibold">{creating ? "Creating…" : "Create & open"}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {isPrivileged && (
          <div data-testid="privileged-banner" className="tracing-border rounded-xl p-5 mb-8 flex items-center gap-4">
            <Crown className="w-6 h-6 text-orange" />
            <div><p className="font-display font-semibold text-slate-50">Complimentary Pro Studio, unlimited</p><p className="text-slate-400 text-sm">As {user.role === "super_admin" ? "super admin" : "an admin"}, all AI production is free & unmetered for you, forever.</p></div>
          </div>
        )}

        {!hasPlan && !isPrivileged && (
          <div data-testid="no-plan-banner" className="rounded-xl p-6 mb-8 border border-amber-400/30 bg-amber-400/5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div><p className="font-display font-semibold text-slate-50">You don't have credits yet</p><p className="text-slate-400 text-sm mt-1">Subscribe to a plan or grab a credit pack to start building. You can create projects for free, AI runs are metered.</p></div>
            <Button data-testid="banner-upgrade-btn" onClick={() => nav("/pricing")} className="rounded-full bg-amber-400 text-black hover:bg-amber-300 font-semibold shrink-0">Choose a plan <ArrowUpRight className="w-4 h-4 ml-1" /></Button>
          </div>
        )}

        {projects.length === 0 ? (
          <div className="card-surface rounded-2xl p-16 text-center">
            <FolderOpen className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="font-display text-xl text-slate-300">No sessions yet</p>
            <p className="text-slate-500 mt-2">Start your first session to feed the Aura engine your brief.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {projects.map((p, i) => (
              <motion.div key={p.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                data-testid={`project-card-${p.id}`} onClick={() => nav(`/studio/${p.id}`)}
                className="card-surface rounded-2xl p-6 text-left hover:border-cyan-400/40 transition-colors group relative cursor-pointer">
                <div className="flex items-center justify-between mb-4">
                  <span className={`label-mono px-2.5 py-1 rounded-full ${p.status === "draft" ? "text-slate-400 bg-white/5" : "text-cyan bg-cyan-400/10"}`}>{p.status.replace("_", " ")}</span>
                  <div className="flex items-center gap-1.5">
                    <button data-testid={`reset-project-${p.id}`} onClick={(e) => reset(e, p.id)} title="Reset project"
                      className="p-1.5 rounded-md text-slate-500 hover:text-amber-400 hover:bg-white/5 transition-colors"><RotateCcw className="w-4 h-4" /></button>
                    <button data-testid={`delete-project-${p.id}`} onClick={(e) => del(e, p.id)} title="Delete project"
                      className="p-1.5 rounded-md text-slate-500 hover:text-red-400 hover:bg-white/5 transition-colors"><Trash2 className="w-4 h-4" /></button>
                    <ArrowUpRight className="w-4 h-4 text-slate-600 group-hover:text-cyan transition-colors" />
                  </div>
                </div>
                <h3 className="font-display font-semibold text-xl text-slate-50">{p.name}</h3>
                <p className="text-slate-500 text-sm mt-1.5 line-clamp-2 h-10">{p.description || "No description"}</p>
                {p.dna && <p className="text-violet text-xs font-mono-a mt-3">DNA · {p.dna.genre} · {p.dna.tempo_bpm} BPM · {p.dna.key}</p>}
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
